import {
  API_AI_CONVERSATION,
  API_AI_CONVERSATION_EXPORT,
  API_AI_CONVERSATION_MESSAGES,
  API_AI_PROJECT,
  API_AI_PROJECT_ARTIFACT,
  API_AI_PROJECT_ARTIFACTS,
  API_AI_PROJECT_CONVERSATIONS,
  API_AI_PROJECT_KNOWLEDGE,
  API_AI_PROJECT_KNOWLEDGE_FILE,
  API_AI_PROJECT_KNOWLEDGE_ITEM,
  API_AI_PROJECT_KNOWLEDGE_UPLOAD,
  API_AI_PROJECT_SHARE,
  API_AI_PROJECT_SHARES,
  API_AI_PROJECTS,
  API_AI_SHAREABLE_GROUPS,
} from 'config/api.url.constants'
import Api from 'services/api'

import type { AssistantStep } from './ai'

export interface AiProject {
  id: number
  name: string
  description: string
  instructions: string
  createdBy: string
  isShared: boolean
  isArchived: boolean
  createdAt: string
  updatedAt: string
  knowledgeCount?: number
  artifactCount?: number
  conversationCount?: number
  /** How many people or groups it is shared with, beyond the company-wide `isShared` switch. */
  shareCount?: number
  /** Whether the caller created it. Only the owner may change who it is shared with. */
  isOwner?: boolean
}

/** A grant of access to a project: one named person, or every member of a user group. */
export interface AiProjectShare {
  id: number
  projectId: number
  subjectType: 'user' | 'group'
  email?: string
  groupId?: number
  /** Display label: the person's email, or the group's name. */
  name: string
  /** Members of a shared group, so the owner can see how far a group grant reaches. */
  memberCount?: number
  createdBy: string
  createdAt: string
}

/** Whether a knowledge item's text could be read; drives the badge in the knowledge panel. */
export type AiKnowledgeStatus = 'ready' | 'unextracted' | 'failed'

export interface AiKnowledgeItem {
  id: number
  projectId: number
  kind: 'file' | 'text'
  name: string
  fileKey?: string
  mimeType?: string
  size: string
  status: AiKnowledgeStatus
  error?: string
  createdBy: string
  createdAt: string
  updatedAt: string
  /** Length of the extracted text. Present on list responses, which omit the text itself. */
  charCount?: number
  /** Only returned when a single item is fetched. */
  content?: string
}

export type AiArtifactKind = 'markdown' | 'text' | 'code' | 'html' | 'json'

export interface AiArtifact {
  id: number
  projectId: number
  title: string
  kind: AiArtifactKind
  language?: string
  content?: string
  version: number
  source: string
  conversationId?: number
  /** The loan it was produced in. Absent means it belongs to the project as a whole. */
  loanNumber?: number
  createdBy: string
  createdAt: string
  updatedAt: string
  charCount?: number
}

export interface AiConversation {
  id: number
  projectId: number | null
  title: string
  /** The loan the thread was started in, when it was started from inside one. */
  loanNumber?: number
  createdBy: string
  /** 'public' chats show in every user's history; 'private' ones only in their creator's. */
  visibility: 'public' | 'private'
  lastMessageAt?: string
  createdAt: string
  updatedAt: string
}

export interface AiConversationMessage {
  id: number
  conversationId: number
  role: 'user' | 'model'
  content: string
  files: Array<{ fileKey: string; name: string }>
  artifactIds: number[]
  steps?: Array<string | AssistantStep>
  /** The turn's tool-call messages, replayed to the model when the thread is reopened. */
  blocks?: any[]
  createdAt: string
}

// ---- Projects ----

export const getAiProjects = (includeArchived = false): Promise<AiProject[]> =>
  Api.get(API_AI_PROJECTS, includeArchived ? { includeArchived: 'true' } : {})

export const getAiProject = (id: number): Promise<AiProject> => Api.get(API_AI_PROJECT, {}, { id })

export const createAiProject = (data: Partial<AiProject>): Promise<AiProject> => Api.post(API_AI_PROJECTS, data)

export const updateAiProject = (id: number, data: Partial<AiProject>): Promise<AiProject> =>
  Api.put(API_AI_PROJECT, data, { id })

export const deleteAiProject = (id: number): Promise<{ success: boolean }> => Api.delete(API_AI_PROJECT, {}, { id })

// ---- Sharing ----

/** User groups a project can be shared with. Names and member counts only - no member emails. */
export interface AiShareableGroup {
  id: number
  name: string
  memberCount: number
}

export const getAiShareableGroups = (): Promise<AiShareableGroup[]> => Api.get(API_AI_SHAREABLE_GROUPS)

export const getAiProjectShares = (id: number): Promise<AiProjectShare[]> =>
  Api.get(API_AI_PROJECT_SHARES, {}, { id })

export const addAiProjectShare = (
  id: number,
  data: { subjectType: 'user' | 'group'; email?: string; groupId?: number },
): Promise<AiProjectShare> => Api.post(API_AI_PROJECT_SHARES, data, { id })

export const removeAiProjectShare = (id: number, shareId: number): Promise<{ success: boolean }> =>
  Api.delete(API_AI_PROJECT_SHARE, {}, { id, shareId })

// ---- Knowledge ----

export const getAiProjectKnowledge = (id: number): Promise<AiKnowledgeItem[]> =>
  Api.get(API_AI_PROJECT_KNOWLEDGE, {}, { id })

export const getAiKnowledgeItem = (id: number, knowledgeId: number): Promise<AiKnowledgeItem> =>
  Api.get(API_AI_PROJECT_KNOWLEDGE_ITEM, {}, { id, knowledgeId })

/** Short-lived URL to the original uploaded file of a 'file' knowledge item. */
export const getAiKnowledgeFileUrl = (id: number, knowledgeId: number): Promise<{ url?: string }> =>
  Api.get(API_AI_PROJECT_KNOWLEDGE_FILE, {}, { id, knowledgeId })

export const uploadAiProjectKnowledge = (id: number, files: File[]): Promise<AiKnowledgeItem[]> =>
  Api.uploadFiles(API_AI_PROJECT_KNOWLEDGE_UPLOAD, {}, { id }, files, { timeout: 0 })

export const addAiProjectKnowledgeText = (id: number, name: string, content: string): Promise<AiKnowledgeItem> =>
  Api.post(API_AI_PROJECT_KNOWLEDGE, { name, content }, { id })

export const updateAiProjectKnowledgeText = (
  id: number,
  knowledgeId: number,
  data: { name?: string; content?: string },
): Promise<AiKnowledgeItem> => Api.put(API_AI_PROJECT_KNOWLEDGE_ITEM, data, { id, knowledgeId })

export const deleteAiProjectKnowledge = (id: number, knowledgeId: number): Promise<{ success: boolean }> =>
  Api.delete(API_AI_PROJECT_KNOWLEDGE_ITEM, {}, { id, knowledgeId })

// ---- Artifacts ----

/** Pass a loan number to get that loan's artifacts plus the project's unscoped ones. */
export const getAiProjectArtifacts = (id: number, loanNumber?: number): Promise<AiArtifact[]> =>
  Api.get(API_AI_PROJECT_ARTIFACTS, loanNumber ? { loanNumber } : {}, { id })

export const getAiProjectArtifact = (id: number, artifactId: number): Promise<AiArtifact> =>
  Api.get(API_AI_PROJECT_ARTIFACT, {}, { id, artifactId })

export const createAiProjectArtifact = (id: number, data: Partial<AiArtifact>): Promise<AiArtifact> =>
  Api.post(API_AI_PROJECT_ARTIFACTS, data, { id })

export const updateAiProjectArtifact = (
  id: number,
  artifactId: number,
  data: Partial<AiArtifact>,
): Promise<AiArtifact> => Api.put(API_AI_PROJECT_ARTIFACT, data, { id, artifactId })

export const deleteAiProjectArtifact = (id: number, artifactId: number): Promise<{ success: boolean }> =>
  Api.delete(API_AI_PROJECT_ARTIFACT, {}, { id, artifactId })

// ---- Conversations ----

/** Pass a loan number to get only the threads started inside that loan. */
export const getAiProjectConversations = (id: number, loanNumber?: number): Promise<AiConversation[]> =>
  Api.get(API_AI_PROJECT_CONVERSATIONS, loanNumber ? { loanNumber } : {}, { id })

export const createAiProjectConversation = (
  id: number,
  title?: string,
  loanNumber?: number,
): Promise<AiConversation> => Api.post(API_AI_PROJECT_CONVERSATIONS, { title, loanNumber }, { id })

export const getAiConversationMessages = (conversationId: number): Promise<AiConversationMessage[]> =>
  Api.get(API_AI_CONVERSATION_MESSAGES, {}, { conversationId })

export type AiConversationExportFormat = 'pdf' | 'docx'

export interface AiConversationExportResult {
  fileName: string
  fileKey: string
  loanNumber: number
}

/** Render selected turns (or the whole thread) as PDF / Word and file them on the loan Documents screen. */
export const exportAiConversation = (
  conversationId: number,
  data: { messageIds?: number[]; format: AiConversationExportFormat; loanNumber?: number },
): Promise<AiConversationExportResult> => Api.post(API_AI_CONVERSATION_EXPORT, data, { conversationId })

/** Owner-only: rename a chat and/or flip it between public and private. */
export const updateAiConversation = (
  conversationId: number,
  data: { title?: string; visibility?: 'public' | 'private' },
): Promise<AiConversation> => Api.put(API_AI_CONVERSATION, data, { conversationId })

export const deleteAiConversation = (conversationId: number): Promise<{ success: boolean }> =>
  Api.delete(API_AI_CONVERSATION, {}, { conversationId })
